import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./payment.css";
import SetupPayment from "./assets/setupPayment.png";
import Header from "../../lawyer/components/header";
import { authenticatedFetch } from "../../network/api";

export default function FinanceDashboard() {
  const [accountCreatePending, setAccountCreatePending] = useState(false);
  const [error, setError] = useState(false);
  const [connectedAccountId, setConnectedAccountId] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(false);
  const [loading, setLoading] = useState(true); // Loading state

  const lawyerInfo = useSelector((state) => state.attorneyData.attorneyData);
  const navigate = useNavigate();

  // Fetch the onboarding status when the component mounts
  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      const user_id = lawyerInfo.user_id;

      try {
        const response = await authenticatedFetch(
          "/api/attorney/webapp/fetchOnboardingStatus",
          {
            method: "POST",
            body: JSON.stringify({ user_id }),
          }
        );

        const data = await response.json();
        const { stripe_account_id, stripe_onboarding_status, error } = data;

        if (error) {
          setError(true);
        } else {
          setConnectedAccountId(stripe_account_id || null);
          setOnboardingStatus(stripe_onboarding_status || false);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false); // Set loading to false after data is fetched
      }
    };

    fetchOnboardingStatus();
  }, [lawyerInfo.user_id]);

  // If content is still loading, show skeleton loader
  if (loading) {
    return (
      <div className="payment-management-container">
        <div className="banner">
          <Skeleton />
        </div>
        <div className="content">
          <Skeleton count={1} />
          <Skeleton count={1} />
          <Skeleton count={1} />
        </div>
      </div>
    );
  }

  // If onboarding is complete, provide button to access Stripe Dashboard
  if (connectedAccountId && onboardingStatus) {
    return (
      <div className="payment-management-container">
        <Header />
        <div className="banner">
          <img
            src={SetupPayment}
            alt="Quidpro Set up Payment"
            className="hero-image"
          />
        </div>
        <div className="content">
          <h2>Your Payment Setup is Complete!</h2>
          <p>
            You can now manage your finances directly through your Stripe
            dashboard.
          </p>

          <button
            className="action-button"
            onClick={() => {
              window.open("https://dashboard.stripe.com/login", "_blank");
            }}
          >
            Go to Stripe Dashboard
          </button>
        </div>
      </div>
    );
  }

  // If onboarding is not complete, show the onboarding message
  return (
    <div className="payment-management-container">
      <Header />
      <div className="banner">
        <img
          src={SetupPayment}
          alt="Quidpro Set up Payment"
          className="hero-image"
        />
      </div>
      <div className="content">
        {!connectedAccountId && (
          <h2 className="warning-text">
            Action Required: Complete Banking Setup
          </h2>
        )}
        {!connectedAccountId && (
          <p className="warning-paragraph">
            Please update your financial information. Employees of this firm
            will not be able to list their services until this step is
            completed.
          </p>
        )}

        {!onboardingStatus && connectedAccountId && (
          <div>
            <h2>Almost There!</h2>
            <p>
              We’ve set up your account! To start accepting payments, we need a
              bit more information.
            </p>
            <button
              className="action-button create-link"
              onClick={() => {
                navigate("/attorney/setup/payment");
              }}
            >
              Finish Onboarding
            </button>
          </div>
        )}

        {error && (
          <p className="error">Something went wrong! Please try again later.</p>
        )}
      </div>
    </div>
  );
}
